import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled, useTheme } from "@mui/material/styles";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { summarizeLostSalesExclusions } from "../../utils/lostSalesErrors";
import { APP_LOCALE } from "../../utils/formatDate";
import {
  REVENUE_BASIS_LABEL,
  describeRevenueEstimate,
  formatCurrencyAmount,
} from "../../utils/lostSalesRevenue";

const formatObservedUnits = (value) =>
  Math.round(Number(value) || 0).toLocaleString();

const formatEstimatedUnits = (value) => {
  const rounded = Math.round((Number(value) || 0) * 10) / 10;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 1,
    maximumFractionDigits: 1,
  });
};

const formatPercent = (value) => `${Math.round((Number(value) || 0) * 100)}%`;

const formatDateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(APP_LOCALE, {
    month: "short",
    day: "numeric",
  });
};

const formatMonthLabel = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(APP_LOCALE, {
    month: "short",
    year: "numeric",
  });
};

const formatMonthFull = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
};

const CHART_MARGIN = { top: 8, right: 12, left: 4, bottom: 0 };

const measureYAxisWidth = (values, formatTick) => {
  const nums = (values || []).map((value) => Number(value) || 0);
  const dataMax = nums.length ? Math.max(...nums) : 0;
  const upper = dataMax <= 0 ? 0 : dataMax * 1.2;
  const ticks = [0, dataMax, upper];
  if (upper > 0) {
    const magnitude = 10 ** Math.max(0, Math.floor(Math.log10(upper)));
    ticks.push(Math.ceil(upper / magnitude) * magnitude);
  }
  let maxWidth = 24;
  const font = "10px Poppins, sans-serif";
  ticks.forEach((tick) => {
    const label = String(formatTick(tick));
    let width = label.length * 7;
    if (typeof document !== "undefined") {
      const canvas =
        measureYAxisWidth.canvas ||
        (measureYAxisWidth.canvas = document.createElement("canvas"));
      const ctx = canvas.getContext("2d");
      ctx.font = font;
      width = ctx.measureText(label).width;
    }
    maxWidth = Math.max(maxWidth, width);
  });
  return Math.ceil(maxWidth) + 14;
};

const tooltipSurfaceSx = {
  px: 1.5,
  py: 1,
  borderRadius: 1,
  bgcolor: "rgba(33, 33, 33, 0.92)",
  color: "#fff",
};

const tooltipTitleSx = {
  fontSize: 11,
  fontWeight: 600,
  mb: 0.25,
  color: "#fff",
};

const tooltipLineSx = {
  fontSize: 11,
  color: "#fff",
};

const formatDateWithYear = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatRange = (start, end) => {
  if (!start || !end) return "";
  return `${formatDateWithYear(start)} – ${formatDateWithYear(end)}`;
};

const productLabel = (product) => {
  if (!product) return "Not available";
  if (typeof product === "string") return product;
  return product.name || product.sku || "Not available";
};

const warehouseLabel = (warehouse) => {
  if (!warehouse) return "Not available";
  if (typeof warehouse === "string") return warehouse;
  return (
    warehouse.warehouse_id ||
    warehouse.warehouse_code ||
    warehouse.name ||
    "Not available"
  );
};

const KpiCard = styled(Paper)(({ theme, accent }) => ({
  padding: theme.spacing(1.75),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `3px solid ${accent || theme.palette.secondary.main}`,
  background: `linear-gradient(135deg, ${accent || theme.palette.secondary.main}0A, ${accent || theme.palette.secondary.main}04)`,
  height: "100%",
}));

/** One headline figure: label, value (+ unit), a short caption, optional note. */
const KpiTile = ({
  accent,
  label,
  value,
  unit,
  valueColor = "text.primary",
  valueTestId,
  caption,
  note,
  noteTestId,
}) => (
  <KpiCard accent={accent}>
    <Typography
      variant="caption"
      sx={{
        fontFamily: "Poppins",
        fontWeight: 600,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: 0.4,
        fontSize: 10,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: "Poppins",
        fontWeight: 700,
        fontSize: "1.6rem",
        lineHeight: 1.2,
        mt: 0.5,
        color: valueColor,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <Box component="span" data-testid={valueTestId}>
        {value}
      </Box>
      {unit && (
        <Box
          component="span"
          sx={{ ml: 0.75, fontSize: "0.85rem", fontWeight: 500, color: "text.secondary" }}
        >
          {unit}
        </Box>
      )}
    </Typography>
    {caption && (
      <Typography
        variant="caption"
        sx={{ display: "block", color: "text.secondary", mt: 0.5, fontFamily: "Poppins" }}
      >
        {caption}
      </Typography>
    )}
    {note && (
      <Typography
        variant="caption"
        data-testid={noteTestId}
        sx={{ display: "block", color: "warning.main", fontWeight: 600, mt: 0.25 }}
      >
        {note}
      </Typography>
    )}
  </KpiCard>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      fontFamily: "Poppins",
      fontWeight: 600,
      mb: 1.25,
      color: "text.primary",
    }}
  >
    {children}
  </Typography>
);

const ChartTooltip = ({
  active,
  payload,
  label,
  labelFormatter,
  mode = "units",
  currency,
}) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper elevation={2} sx={tooltipSurfaceSx}>
      <Typography sx={tooltipTitleSx}>
        {labelFormatter ? labelFormatter(label) : label}
      </Typography>
      {mode === "revenue" ? (
        <>
          <Typography sx={tooltipLineSx}>
            Est. lost revenue: {formatCurrencyAmount(point.revenue, currency)}
          </Typography>
          <Typography sx={tooltipLineSx}>
            Lost units: {formatEstimatedUnits(point.lost)}
          </Typography>
        </>
      ) : (
        <Typography sx={tooltipLineSx}>
          Lost sales: {formatEstimatedUnits(payload[0]?.value)} units
        </Typography>
      )}
    </Paper>
  );
};

const InventoryChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper elevation={2} sx={tooltipSurfaceSx}>
      <Typography sx={tooltipTitleSx}>{formatDateLabel(label)}</Typography>
      <Typography sx={tooltipLineSx}>
        Inventory: {formatObservedUnits(point.inventory)} units
      </Typography>
      <Typography sx={tooltipLineSx}>
        Expected demand: {formatEstimatedUnits(point.expected)} units
      </Typography>
      {point.insufficient && (
        <Typography sx={{ ...tooltipLineSx, mt: 0.25, color: "#ffcc80" }}>
          {point.stockout ? "Stockout" : "Inventory below demand"}
        </Typography>
      )}
    </Paper>
  );
};

const MonthlyImpactTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper elevation={2} sx={tooltipSurfaceSx}>
      <Typography sx={tooltipTitleSx}>{formatMonthFull(label)}</Typography>
      <Typography sx={tooltipLineSx}>
        Lost Sales: {formatEstimatedUnits(point.lost)} units
      </Typography>
      <Typography sx={tooltipLineSx}>
        Expected Demand: {formatEstimatedUnits(point.expected)} units
      </Typography>
      <Typography sx={tooltipLineSx}>
        Actual Sales: {formatObservedUnits(point.actual)} units
      </Typography>
      <Typography sx={tooltipLineSx}>
        Stockout Days: {formatObservedUnits(point.stockoutDays)}
      </Typography>
    </Paper>
  );
};

const ChartLegendSwatch = ({ dashed, color, fill }) => (
  <Box
    sx={{
      width: 14,
      height: 8,
      borderTop: dashed ? `2px dashed ${color}` : "none",
      bgcolor: dashed ? "transparent" : fill || color,
      borderRadius: dashed ? 0 : 0.25,
    }}
  />
);

const LostSalesResultView = ({
  result,
  selectedInventory,
  showHeading = false,
}) => {
  const theme = useTheme();
  const [dailyExpanded, setDailyExpanded] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());
  const [trendMode, setTrendMode] = useState("units");

  const metrics = result?.metrics;
  const scope = result?.scope;
  const metadata = result?.metadata;
  const isMock = metadata?.is_mock;
  const revenueEstimate = result?.revenue_estimate;
  const revenueView = describeRevenueEstimate(revenueEstimate);
  const revenueAvailable = revenueEstimate?.estimated_lost_revenue != null;
  const revenueCurrency = revenueEstimate?.currency;
  const trend = result?.breakdown?.trend || [];
  const excluded = result?.breakdown?.excluded || [];
  const exclusionSummary = summarizeLostSalesExclusions(excluded);

  const productName = productLabel(selectedInventory?.product);
  const warehouseName = warehouseLabel(selectedInventory?.warehouse);
  const isSkuLevel = scope?.analysis_level === "sku";
  const isProductLevel = scope?.analysis_level === "product";
  const childSkuCount = Number(scope?.child_sku_count) || 0;
  const skuName = scope?.sku || selectedInventory?.sku || "Not available";
  const dateRange = formatRange(scope?.start_date, scope?.end_date);
  const participatingSkus = useMemo(() => {
    const skus = new Set();
    [...trend, ...excluded].forEach((row) => {
      const coverage = row.inventory_coverage || {};
      (coverage.resolved_skus || []).forEach((sku) => {
        if (sku) skus.add(sku);
      });
      (coverage.missing_skus || []).forEach((sku) => {
        if (sku) skus.add(sku);
      });
    });
    if (skus.size === 0) {
      (selectedInventory?.product?.variants || []).forEach((variant) => {
        if (variant?.sku) skus.add(variant.sku);
      });
    }
    return [...skus].sort((left, right) => left.localeCompare(right));
  }, [trend, excluded, selectedInventory]);

  const chartColor = theme.palette.error.main;
  const secondaryColor = theme.palette.secondary.main;
  const inventoryColor = theme.palette.info.main;
  const demandColor = theme.palette.text.secondary;
  const insufficientFill = `${theme.palette.warning.main}1F`;

  const trendChartData = useMemo(
    () =>
      trend.map((row) => ({
        date: row.day || row.period,
        lost: Math.round((Number(row.estimated_lost_units) || 0) * 10) / 10,
        // Full precision; rounded only when formatted.
        revenue: row.estimated_lost_revenue ?? null,
      })),
    [trend],
  );
  const showRevenueTrend = trendMode === "revenue" && revenueAvailable;

  const inventoryChartData = useMemo(
    () =>
      trend.map((row) => {
        const inventory = Number(row.fulfillable_inventory) || 0;
        const expected =
          Math.round((Number(row.expected_units) || 0) * 10) / 10;
        const stockout = Boolean(row.stockout) || inventory === 0;
        return {
          date: row.day || row.period,
          inventory,
          expected,
          stockout,
          insufficient: stockout || inventory < expected,
        };
      }),
    [trend],
  );

  const insufficientRanges = useMemo(() => {
    const ranges = [];
    let start = null;
    let last = null;
    inventoryChartData.forEach((row) => {
      if (row.insufficient) {
        if (!start) start = row.date;
        last = row.date;
        return;
      }
      if (start) {
        ranges.push({ x1: start, x2: last });
        start = null;
        last = null;
      }
    });
    if (start) ranges.push({ x1: start, x2: last });
    return ranges;
  }, [inventoryChartData]);

  const monthlyBreakdown = useMemo(() => {
    const byMonth = new Map();
    trend.forEach((row) => {
      const dateKey = row.day || row.period;
      const month = String(dateKey || "").slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(month)) return;
      const entry = byMonth.get(month) || {
        month,
        expected_units: 0,
        actual_units_sold: 0,
        estimated_lost_units: 0,
        stockout_days: 0,
        days: [],
      };
      entry.expected_units += Number(row.expected_units) || 0;
      entry.actual_units_sold += Number(row.actual_units_sold) || 0;
      entry.estimated_lost_units += Number(row.estimated_lost_units) || 0;
      entry.stockout_days += row.stockout ? 1 : 0;
      entry.days.push(row);
      byMonth.set(month, entry);
    });
    return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
  }, [trend]);

  const monthlyChartData = useMemo(
    () =>
      monthlyBreakdown.map((row) => ({
        month: row.month,
        lost: Math.round((Number(row.estimated_lost_units) || 0) * 10) / 10,
        expected: row.expected_units,
        actual: row.actual_units_sold,
        stockoutDays: row.stockout_days,
      })),
    [monthlyBreakdown],
  );

  const lostSalesYAxisWidth = useMemo(
    () =>
      showRevenueTrend
        ? measureYAxisWidth(
            trendChartData.map((row) => row.revenue),
            (value) =>
              formatCurrencyAmount(value, revenueCurrency, { compact: true }),
          )
        : measureYAxisWidth(
            trendChartData.map((row) => row.lost),
            formatEstimatedUnits,
          ),
    [trendChartData, showRevenueTrend, revenueCurrency],
  );

  const inventoryYAxisWidth = useMemo(
    () =>
      measureYAxisWidth(
        inventoryChartData.flatMap((row) => [row.inventory, row.expected]),
        formatObservedUnits,
      ),
    [inventoryChartData],
  );

  const monthlyYAxisWidth = useMemo(
    () =>
      measureYAxisWidth(
        monthlyChartData.map((row) => row.lost),
        formatEstimatedUnits,
      ),
    [monthlyChartData],
  );

  const topImpactDays = useMemo(
    () =>
      [...trend]
        .sort(
          (a, b) =>
            (Number(b.estimated_lost_units) || 0) -
            (Number(a.estimated_lost_units) || 0),
        )
        .slice(0, 5)
        .filter((row) => (Number(row.estimated_lost_units) || 0) > 0),
    [trend],
  );

  const toggleMonth = (month) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(month)) next.delete(month);
      else next.add(month);
      return next;
    });
  };

  if (result?.status !== "complete") return null;

  return (
    <Box>
        {isMock && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Mock Analysis / Development Only — no historical sales or inventory
            data was queried.
          </Alert>
        )}

        {metrics && (
          <Box sx={{ display: "grid", gap: 2.5 }}>
            <Box>
              {showHeading && (
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: 14,
                    mb: 1,
                    color: "text.primary",
                  }}
                >
                  Analysis Results
                </Typography>
              )}
              {isSkuLevel ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    SKU: {skuName}
                    <Box
                      component="span"
                      sx={{ color: "text.secondary", fontWeight: 400 }}
                    >
                      {" · "}
                      {warehouseName}
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      color: "text.secondary",
                    }}
                  >
                    Product: {productName}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                >
                  Product: {productName}
                  {isProductLevel &&
                    childSkuCount > 0 &&
                    !(showHeading && participatingSkus.length === 1) && (
                    <Box
                      component="span"
                      sx={{ color: "text.secondary", fontWeight: 400 }}
                    >
                      {` · ${childSkuCount} child SKU${
                        childSkuCount === 1 ? "" : "s"
                      }`}
                    </Box>
                  )}
                  {showHeading &&
                    isProductLevel &&
                    participatingSkus.length === 1 && (
                    <Box
                      component="span"
                      sx={{ color: "text.secondary", fontWeight: 400 }}
                    >
                      {` · ${participatingSkus[0]}`}
                    </Box>
                  )}
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", fontWeight: 400 }}
                  >
                    {" · "}
                    {warehouseName}
                  </Box>
                </Typography>
              )}
              {dateRange && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontFamily: "Poppins",
                    ...(showHeading
                      ? { display: "block", mt: 0.25 }
                      : {}),
                  }}
                >
                  Analyzed {dateRange}
                </Typography>
              )}
              {isProductLevel &&
                participatingSkus.length > (showHeading ? 1 : 0) && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    mt: showHeading ? 0.75 : 1,
                    maxHeight: 72,
                    overflow: "auto",
                  }}
                >
                  {participatingSkus.map((sku) => (
                    <Box
                      key={sku}
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: "action.hover",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontFamily: "Poppins" }}
                      >
                        {sku}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box
              data-testid="lost-sales-kpis"
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 1.5,
              }}
            >
              <KpiTile
                accent={theme.palette.error.main}
                label="Estimated Lost Sales"
                value={formatObservedUnits(metrics.estimated_lost_units)}
                unit="units"
                valueColor="error.main"
                caption="Sales missed while stock ran short"
              />
              <KpiTile
                accent={theme.palette.text.secondary}
                label="Actual Sales"
                value={formatObservedUnits(metrics.actual_units_sold)}
                unit="units"
                caption="Units sold in the period"
              />
              <KpiTile
                accent={theme.palette.primary.main}
                label="Estimated Lost Revenue"
                value={revenueView.amountLabel}
                valueTestId="lost-revenue-kpi"
                caption={`Estimate ${REVENUE_BASIS_LABEL}`}
                note={revenueView.note}
                noteTestId="lost-revenue-note"
              />
              <KpiTile
                accent={theme.palette.warning.main}
                label="Stockout Days"
                value={formatObservedUnits(metrics.stockout_days)}
                unit={metrics.stockout_days === 1 ? "day" : "days"}
                caption={
                  metrics.valid_analysis_days
                    ? `Of ${formatObservedUnits(metrics.valid_analysis_days)} analyzed days`
                    : "Days with no sellable stock"
                }
              />
            </Box>
            <Typography
              variant="caption"
              data-testid="lost-sales-daily-note"
              sx={{ display: "block", color: "text.secondary", mt: -0.5 }}
            >
              Lost sales are counted day by day, on days when sellable stock fell
              short of that day&apos;s expected demand.
            </Typography>

            {trendChartData.length > 0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <SectionTitle>
                    {showRevenueTrend
                      ? `Estimated Lost Revenue Over Time (${revenueCurrency})`
                      : "Lost Sales Over Time"}
                  </SectionTitle>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={showRevenueTrend ? "revenue" : "units"}
                    onChange={(_event, value) => value && setTrendMode(value)}
                    aria-label="Trend measure"
                    sx={{ mb: 1 }}
                  >
                    <ToggleButton value="units" sx={{ textTransform: "none", px: 1.25, py: 0.25 }}>
                      Units
                    </ToggleButton>
                    <ToggleButton
                      value="revenue"
                      disabled={!revenueAvailable}
                      title={
                        revenueAvailable
                          ? undefined
                          : revenueView.note || "Revenue estimate unavailable"
                      }
                      sx={{ textTransform: "none", px: 1.25, py: 0.25 }}
                    >
                      Revenue
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {showRevenueTrend && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.secondary", mb: 0.75 }}
                  >
                    Lost units x current catalog price. Not historical revenue.
                    {revenueView.note ? ` ${revenueView.note}` : ""}
                  </Typography>
                )}
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, height: 210 }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendChartData} margin={CHART_MARGIN}>
                      <defs>
                        <linearGradient
                          id="lostSalesTrendFill"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor={chartColor}
                            stopOpacity={0.28}
                          />
                          <stop
                            offset="100%"
                            stopColor={chartColor}
                            stopOpacity={0.03}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke={theme.palette.divider}
                      />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDateLabel}
                        minTickGap={28}
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fontSize: 10,
                          fill: theme.palette.text.secondary,
                        }}
                      />
                      <YAxis
                        allowDecimals
                        axisLine={false}
                        tickLine={false}
                        width={lostSalesYAxisWidth}
                        tick={{
                          fontSize: 10,
                          fill: theme.palette.text.secondary,
                        }}
                        tickFormatter={(value) =>
                          showRevenueTrend
                            ? formatCurrencyAmount(value, revenueCurrency, {
                                compact: true,
                              })
                            : formatEstimatedUnits(value)
                        }
                      />
                      <Tooltip
                        content={
                          <ChartTooltip
                            labelFormatter={formatDateLabel}
                            mode={showRevenueTrend ? "revenue" : "units"}
                            currency={revenueCurrency}
                          />
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey={showRevenueTrend ? "revenue" : "lost"}
                        stroke={chartColor}
                        fill="url(#lostSalesTrendFill)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 3 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>
            )}

            {inventoryChartData.length > 0 && (
              <Box>
                <SectionTitle>Inventory vs Demand</SectionTitle>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.75,
                    mb: 1,
                    px: 0.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <ChartLegendSwatch color={inventoryColor} />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Fulfillable inventory
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <ChartLegendSwatch dashed color={demandColor} />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Expected demand
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <ChartLegendSwatch
                      color={theme.palette.warning.main}
                      fill={insufficientFill}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Below demand / stockout
                    </Typography>
                  </Box>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, height: 210 }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={inventoryChartData}
                      margin={CHART_MARGIN}
                    >
                      <defs>
                        <linearGradient
                          id="inventoryTrendFill"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor={inventoryColor}
                            stopOpacity={0.28}
                          />
                          <stop
                            offset="100%"
                            stopColor={inventoryColor}
                            stopOpacity={0.03}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke={theme.palette.divider}
                      />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDateLabel}
                        minTickGap={28}
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fontSize: 10,
                          fill: theme.palette.text.secondary,
                        }}
                      />
                      <YAxis
                        allowDecimals
                        axisLine={false}
                        tickLine={false}
                        width={inventoryYAxisWidth}
                        tick={{
                          fontSize: 10,
                          fill: theme.palette.text.secondary,
                        }}
                        tickFormatter={(value) => formatObservedUnits(value)}
                      />
                      <Tooltip content={<InventoryChartTooltip />} />
                      {insufficientRanges.map((range) => (
                        <ReferenceArea
                          key={`${range.x1}-${range.x2}`}
                          x1={range.x1}
                          x2={range.x2}
                          fill={theme.palette.warning.main}
                          fillOpacity={0.12}
                        />
                      ))}
                      <Area
                        type="monotone"
                        dataKey="inventory"
                        stroke={inventoryColor}
                        fill="url(#inventoryTrendFill)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 3 }}
                        name="Fulfillable inventory"
                      />
                      <Line
                        type="monotone"
                        dataKey="expected"
                        stroke={demandColor}
                        strokeWidth={1.5}
                        strokeDasharray="4 4"
                        dot={false}
                        activeDot={{ r: 3 }}
                        name="Expected demand"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>
            )}

            {monthlyChartData.length > 0 && (
              <Box>
                <SectionTitle>Monthly Impact</SectionTitle>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, height: 190 }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyChartData} margin={CHART_MARGIN}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke={theme.palette.divider}
                      />
                      <XAxis
                        dataKey="month"
                        tickFormatter={formatMonthLabel}
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fontSize: 10,
                          fill: theme.palette.text.secondary,
                        }}
                      />
                      <YAxis
                        allowDecimals
                        axisLine={false}
                        tickLine={false}
                        width={monthlyYAxisWidth}
                        tick={{
                          fontSize: 10,
                          fill: theme.palette.text.secondary,
                        }}
                        tickFormatter={(value) => formatEstimatedUnits(value)}
                      />
                      <Tooltip content={<MonthlyImpactTooltip />} />
                      <Bar
                        dataKey="lost"
                        fill={secondaryColor}
                        radius={[6, 6, 0, 0]}
                        maxBarSize={48}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>
            )}

            {topImpactDays.length > 0 && (
              <Box>
                <SectionTitle>Highest-Impact Days</SectionTitle>
                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 2, overflow: "hidden" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                          Date
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Expected
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Actual
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Inventory
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Lost
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topImpactDays.map((row) => (
                        <TableRow key={row.day || row.period}>
                          <TableCell sx={{ fontSize: 12 }}>
                            {formatDateLabel(row.day || row.period)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 12 }}>
                            {formatEstimatedUnits(row.expected_units)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 12 }}>
                            {formatObservedUnits(row.actual_units_sold)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 12 }}>
                            {formatObservedUnits(row.fulfillable_inventory)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "error.main",
                            }}
                          >
                            {formatEstimatedUnits(row.estimated_lost_units)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            )}

            {monthlyBreakdown.length > 0 && (
              <Accordion
                disableGutters
                elevation={0}
                expanded={dailyExpanded}
                onChange={(_, expanded) => setDailyExpanded(expanded)}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px !important",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 44,
                    "& .MuiAccordionSummary-content": { my: 0.75 },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "Poppins", fontWeight: 600 }}
                  >
                    View monthly breakdown
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ width: 36, fontWeight: 600, fontSize: 12 }}
                        />
                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                          Month
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Expected
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Actual
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Lost Sales
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, fontSize: 12 }}
                        >
                          Stockout Days
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthlyBreakdown.map((group) => {
                        const isOpen = expandedMonths.has(group.month);
                        return (
                          <React.Fragment key={group.month}>
                            <TableRow
                              hover
                              onClick={() => toggleMonth(group.month)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell sx={{ width: 36, py: 0.5 }}>
                                <IconButton
                                  size="small"
                                  aria-label={
                                    isOpen
                                      ? `Collapse ${formatMonthFull(group.month)}`
                                      : `Expand ${formatMonthFull(group.month)}`
                                  }
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleMonth(group.month);
                                  }}
                                >
                                  <ExpandMoreIcon
                                    sx={{
                                      fontSize: 20,
                                      transform: isOpen
                                        ? "rotate(0deg)"
                                        : "rotate(-90deg)",
                                      transition: "transform 150ms",
                                    }}
                                  />
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
                                {formatMonthFull(group.month)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12 }}>
                                {formatEstimatedUnits(group.expected_units)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12 }}>
                                {formatObservedUnits(group.actual_units_sold)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "error.main",
                                }}
                              >
                                {formatEstimatedUnits(
                                  group.estimated_lost_units,
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12 }}>
                                {formatObservedUnits(group.stockout_days)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                sx={{
                                  py: 0,
                                  px: 0,
                                  borderBottom: isOpen ? undefined : "none",
                                  bgcolor: "action.hover",
                                }}
                              >
                                <Collapse
                                  in={isOpen}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box
                                    sx={{
                                      mx: 1.5,
                                      my: 1,
                                      maxHeight: 240,
                                      overflow: "auto",
                                      border: `1px solid ${theme.palette.divider}`,
                                      borderRadius: 1,
                                      bgcolor: "background.paper",
                                    }}
                                  >
                                    <Table size="small" stickyHeader>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: 12,
                                              bgcolor: "background.paper",
                                            }}
                                          >
                                            Date
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: 12,
                                              bgcolor: "background.paper",
                                            }}
                                          >
                                            Expected
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: 12,
                                              bgcolor: "background.paper",
                                            }}
                                          >
                                            Actual
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: 12,
                                              bgcolor: "background.paper",
                                            }}
                                          >
                                            Inventory
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: 12,
                                              bgcolor: "background.paper",
                                            }}
                                          >
                                            Lost Sales
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {group.days.map((row) => (
                                          <TableRow
                                            key={`daily-${row.day || row.period}`}
                                          >
                                            <TableCell sx={{ fontSize: 12 }}>
                                              {formatDateLabel(
                                                row.day || row.period,
                                              )}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              sx={{ fontSize: 12 }}
                                            >
                                              {formatEstimatedUnits(
                                                row.expected_units,
                                              )}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              sx={{ fontSize: 12 }}
                                            >
                                              {formatObservedUnits(
                                                row.actual_units_sold,
                                              )}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              sx={{ fontSize: 12 }}
                                            >
                                              {formatObservedUnits(
                                                row.fulfillable_inventory,
                                              )}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: "error.main",
                                              }}
                                            >
                                              {formatEstimatedUnits(
                                                row.estimated_lost_units,
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            )}

            <Box
              sx={{
                pt: 1.5,
                mt: 0.5,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "text.secondary",
                  mb: 1,
                }}
              >
                Analysis details
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  color: "text.secondary",
                  mb: 0.5,
                }}
              >
                Methodology
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: "block", color: "text.secondary", mb: 0.5 }}
              >
                For each day, lost sales are the portion of that day&apos;s
                expected demand not covered by either actual sales or the
                available fulfillable inventory. Daily results are then summed,
                so a day that sold above expectation never offsets a stockout
                day.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  mb: 1.5,
                  fontFamily: "Poppins",
                }}
              >
                Daily Lost Sales = max(0, Expected Demand − max(Actual Sales,
                Fulfillable Inventory))
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: "block", color: "text.secondary", mb: 1.5 }}
              >
                Estimated Lost Revenue = Estimated Lost Units × current selling
                price from the product catalog. Historical prices are not
                available, so this is an estimate, not realized revenue.
                Units with no usable price are excluded, and amounts in
                different currencies are never combined.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  color: "text.secondary",
                  mb: 0.75,
                }}
              >
                Data details
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Days analyzed:{" "}
                  <strong>
                    {formatObservedUnits(metrics.valid_analysis_days)}
                  </strong>
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Data coverage:{" "}
                  <strong>{formatPercent(metrics.data_coverage)}</strong>
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Excluded days:{" "}
                  <strong>{formatObservedUnits(metrics.excluded_days)}</strong>
                </Typography>
              </Box>
              {metrics.excluded_days > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "text.secondary",
                    mb: 0.75,
                  }}
                >
                  {metrics.valid_analysis_days} of{" "}
                  {metrics.valid_analysis_days + metrics.excluded_days} days
                  were analyzed
                  {exclusionSummary ? ` (${exclusionSummary})` : ""}.
                </Typography>
              )}
              {metadata?.incomplete_inventory_days > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "text.secondary",
                  }}
                >
                  {metadata.incomplete_inventory_days} analyzed day
                  {metadata.incomplete_inventory_days === 1 ? "" : "s"} had
                  incomplete child-SKU inventory coverage.
                </Typography>
              )}
            </Box>
          </Box>
        )}
    </Box>
  );
};

export default LostSalesResultView;
